import StyledButton from '@/components/styled/StyledButton';
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import StyledTooltipContainer from '@/components/styled/StyledTooltipContainer';
import StyledInput from '@/components/styled/StyledInput';
import { Label } from '@/components/ui/label';
import StyledRadioGroup from '@/components/styled/StyledRadioGroup';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableInput from '@/components/styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '@/components/styled/StyledTranslatableTextarea';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useMemo } from 'react';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';

interface CategoryOptionListProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const CategoryOptionList = ({
  control,
  index,
  language,
  isViewMode = false,
}: CategoryOptionListProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `carCategories.${index}.options`,
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: `carCategories.${index}.options`,
    });
  return (
    <>
      {fields.map((item, optionIndex) => (
        <CategoryOptionsCard
          key={item.id}
          control={control}
          categoryIndex={index}
          optionIndex={optionIndex}
          language={language}
          deleteOption={() => {
            remove(optionIndex);
            removeTranslation(optionIndex);
          }}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('carRental.categoryForm.addOption')}
          onClick={() => {
            append({
              name: undefined,
              description: undefined,
              amount: undefined,
              billingType: undefined,
            } as CarRentalFormDataWithTranslations['carCategories'][number]['options'][number]);
            appendTranslation(() => ({
              name: undefined,
              description: undefined,
            }));
          }}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </>
  );
};

export default CategoryOptionList;

interface CategoryOptionsCardProps {
  control: Control<CarRentalFormDataWithTranslations>;
  categoryIndex: number;
  optionIndex: number;
  language: SupportedLocale;
  deleteOption: () => void;
  isViewMode?: boolean;
}

const CategoryOptionsCard = ({
  control,
  categoryIndex,
  optionIndex,
  language,
  deleteOption,
  isViewMode = false,
}: CategoryOptionsCardProps) => {
  const { t } = useTranslation();
  const currentTitle = useWatch({
    control,
    name: `carCategories.${categoryIndex}.options.${optionIndex}.name`,
  });
  const translations = useWatch({
    control,
    name: 'translations',
  });
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const cardTitle = useMemo(() => {
    const currentTranslation =
      translations?.[language]?.carCategories?.[categoryIndex]?.options?.[
        optionIndex
      ];
    const currentTitleTranslated = currentTranslation?.name ?? currentTitle;
    return (
      currentTitleTranslated ||
      currentTitle ||
      t('carRental.categoryForm.option', { index: optionIndex + 1 })
    );
  }, [currentTitle, categoryIndex, optionIndex, language, t, translations]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle className="text-base leading-6 hover:cursor-pointer">
          {cardTitle}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {optionIndex > 0 && !isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.categoryForm.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => deleteOption()}
                />
              </StyledTooltipContainer>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <StyledTranslatableInput
            control={control}
            originalName={`carCategories.${categoryIndex}.options.${optionIndex}.name`}
            title={t('carRental.categoryForm.name')}
            name={`carCategories.${categoryIndex}.options.${optionIndex}.name`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.categoryForm.namePlaceholder')}
            disabled={isViewMode}
          />
          <StyledTranslatableTextarea
            control={control}
            originalName={`carCategories.${categoryIndex}.options.${optionIndex}.description`}
            title={t('carRental.categoryForm.optionDescription')}
            name={`carCategories.${categoryIndex}.options.${optionIndex}.description`}
            setValue={setValue}
            language={language}
            placeholder={t(
              'carRental.categoryForm.optionDescriptionPlaceholder',
            )}
            rows={3}
            disabled={isViewMode}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700">
              {t('carRental.categoryForm.amount')}
            </Label>
            <div className="flex gap-6 w-full items-center">
              <div className="min-w-20 max-w-40">
                <StyledInput
                  control={control}
                  name={`carCategories.${categoryIndex}.options.${optionIndex}.amount`}
                  placeholder={t('carRental.categoryForm.amountPlaceholder')}
                  type="number"
                  min={0}
                  disabled={isViewMode}
                />
              </div>
              <StyledRadioGroup
                control={control}
                name={`carCategories.${categoryIndex}.options.${optionIndex}.billingType`}
                options={[
                  {
                    label: t('carRental.categoryForm.billingTypePerDay'),
                    value: 'perDay',
                  },
                  {
                    label: t('carRental.categoryForm.billingTypePerRental'),
                    value: 'perRental',
                  },
                ]}
                className="w-1/2"
                disabled={isViewMode}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
